import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebaseConfig';
import { initCSRFProtection } from './utils/csrfProtection';
import ErrorBoundary from './components/ErrorBoundary';
import TeamRequiredRoute from './components/TeamRequiredRoute';
import BetaApprovedRoute from './components/BetaApprovedRoute';


import StrategyRoom from './features/strategyroom/view/StrategyRoomView';
import MyPlaybook from './pages/MyPlaybook';
import StrategyViewer from './pages/StrategyViewer';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import Recovery from './pages/Recovery';
import SuperAdmin from './superadmin/SuperAdmin';
import SettingsView from './features/settings/view/SettingsView';
import MyStatistics from './components/MyStats/MyStatistics';
import JoinTeam from './pages/JoinTeam';
import CreateTeam from './pages/CreateTeam';
import TeamDecision from './pages/TeamDecision';
import ScrimCalendar from './pages/ScrimCalendar';
import LandingPage from './pages/LandingPage';
import Community from './pages/Community';
import LegalTerms from './pages/LegalTerms';
import LegalPrivacy from './pages/LegalPrivacy';
import LegalRefund from './pages/LegalRefund';
import SubmitTeamSummaryForm from './components/SubmitTeamSummaryForm';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddPlayerStatsForm from './components/AddPlayerStatsForm';
import AddEnemyDashboard from './components/AddEnemyDashboard';
import DemoPlaybook from './pages/DemoPlaybook';
import DemoStrategy from './pages/DemoStrategy';
import PaymentSuccess from './pages/PaymentSuccess';

// ✅ ProtectedRoute: for logged-in users only (with email verification)
const ProtectedRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isAuth, setIsAuth] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // ✅ FIXED: Require email verification for protected routes
      setIsAuth(!!user && !!user.emailVerified);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) return null;
  return isAuth ? children : <Navigate to="/login" />;
};

// ✅ SuperAdmin-only route
const SuperAdminRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isAllowed, setIsAllowed] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsAllowed(false);
        setLoading(false);
        return;
      }
      try {
        const token = await user.getIdTokenResult();
        if (token.claims?.superadmin) {
          setIsAllowed(true);
        } else {
          // Fallback: check Firestore user role
          const userRef = doc(db, `users/${user.uid}`);
          const userSnap = await getDoc(userRef);
          const role = userSnap.exists() ? userSnap.data()?.role : null;
          setIsAllowed(role === 'superadmin');
        }
      } catch (err) {
        console.error('Error checking superadmin claim', err);
        setIsAllowed(false);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  if (loading) return null;
  return isAllowed ? children : <Navigate to="/login" />;
};

// ✅ PublicOnlyRoute: for non-logged-in users only
const PublicOnlyRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [redirectPath, setRedirectPath] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setRedirectPath('');
        setLoading(false);
        return;
      }
      try {
        const token = await user.getIdTokenResult();
        if (token.claims?.superadmin) {
          setRedirectPath('/superadmin');
        } else {
          // Fallback: check Firestore user role
          const userRef = doc(db, `users/${user.uid}`);
          const userSnap = await getDoc(userRef);
          const role = userSnap.exists() ? userSnap.data()?.role : null;
          setRedirectPath(role === 'superadmin' ? '/superadmin' : '/my-strategy');
        }
      } catch (err) {
        console.error('Error checking claims', err);
        setRedirectPath('/landing');
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  if (loading) return null;
  return !redirectPath ? children : <Navigate to={redirectPath} />;
};

function App() {
  // ✅ Initialize CSRF protection on app load
  React.useEffect(() => {
    initCSRFProtection();
  }, []);

  return (
    <ErrorBoundary>
      <div style={{ position: 'relative' }}>
        <Router>
          <Routes>

          {/* ✅ Landing page is always accessible */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/community" element={<Community />} />
          <Route path="/legal/terms" element={<LegalTerms />} />
          <Route path="/legal/privacy" element={<LegalPrivacy />} />
          <Route path="/legal/refund" element={<LegalRefund />} />
          <Route path="/demo" element={<DemoPlaybook />} />
          <Route path="/demo/:demoId" element={<DemoStrategy />} />
          <Route path="/payment/success" element={
            <ProtectedRoute>
              <PaymentSuccess />
            </ProtectedRoute>
          } />

          {/* ✅ Public-only routes */}
          <Route path="/login" element={
            <PublicOnlyRoute>
              <Login />
            </PublicOnlyRoute>
          } />

          <Route path="/recovery" element={
            <PublicOnlyRoute>
              <Recovery />
            </PublicOnlyRoute>
          } />

          <Route path="/register" element={
  <PublicOnlyRoute>
    <Register />
  </PublicOnlyRoute>
} />

          <Route path="/settings" element={
            <ProtectedRoute>
              <SettingsView />
            </ProtectedRoute>
          } />

          <Route path="/superadmin" element={
            <SuperAdminRoute>
              <SuperAdmin />
            </SuperAdminRoute>
          } />

<Route path="/verify-email" element={
  <ProtectedRoute>
    <VerifyEmail />
  </ProtectedRoute>
} />
          {/* 🚫 Blocked routes */}
          

          {/* ✅ Protected routes that require a team */}
          <Route path="/my-stats" element={
            <ProtectedRoute>
              <TeamRequiredRoute>
                <MyStatistics />
              </TeamRequiredRoute>
            </ProtectedRoute>
          } />

           <Route path="/submit-enemy-stats" element={
            <ProtectedRoute>
              <TeamRequiredRoute>
                <AddEnemyDashboard />
              </TeamRequiredRoute>
            </ProtectedRoute>
          } />

            {/* ✅ Protected routes */}
          <Route path="/addPlayerStatsForm" element={
            <ProtectedRoute>
              <TeamRequiredRoute>
                <AddPlayerStatsForm />
              </TeamRequiredRoute>
            </ProtectedRoute>
          } />

          
           {/* ✅ Protected routes */}
          <Route path="/submit-team-summary" element={
            <ProtectedRoute>
              <TeamRequiredRoute>
                <SubmitTeamSummaryForm />
              </TeamRequiredRoute>
            </ProtectedRoute>
          } />

         
          <Route path="/create-team" element={
            <ProtectedRoute>
              <BetaApprovedRoute>
                <CreateTeam />
              </BetaApprovedRoute>
            </ProtectedRoute>
          } />
          <Route path="/team-decision" element={
            <ProtectedRoute>
              <TeamDecision />
            </ProtectedRoute>
          } />
          <Route path="/join-team" element={
            <ProtectedRoute>
              <JoinTeam />
            </ProtectedRoute>
          } />
        
          <Route path="/viewer" element={
            <ProtectedRoute>
              <StrategyViewer />
            </ProtectedRoute>
          } />
          <Route path="/my-calendar" element={
            <ProtectedRoute>
              <TeamRequiredRoute>
                <ScrimCalendar />
              </TeamRequiredRoute>
            </ProtectedRoute>
          } />
          <Route path="/viewer/:strategyId" element={
            <ProtectedRoute>
              <TeamRequiredRoute>
                <StrategyRoom isViewerMode={true} />
              </TeamRequiredRoute>
            </ProtectedRoute>
          } />
          <Route path="/my-playbook" element={
            <ProtectedRoute>
              <TeamRequiredRoute>
                <MyPlaybook />
              </TeamRequiredRoute>
            </ProtectedRoute>
          } />
          <Route path="/my-strategy" element={
            <ProtectedRoute>
              <TeamRequiredRoute>
                <StrategyRoom />
              </TeamRequiredRoute>
            </ProtectedRoute>
          } />
          <Route path="/strategy/:strategyId" element={
            <ProtectedRoute>
              <TeamRequiredRoute>
                <StrategyRoom />
              </TeamRequiredRoute>
            </ProtectedRoute>
          } />
        </Routes>
      </Router>

      {/* ✅ Ghost drag preview container */}
      <div
        id="ghost-drag-preview"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '1px',
          height: '1px',
          opacity: 0,
          pointerEvents: 'none'
        }}
      />

      {/* ✅ Toast container */}
      <ToastContainer
        position="bottom-right"
        autoClose={2500}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      </div>
    </ErrorBoundary>
  );
}

export default App;
